import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Zap, 
  Users, 
  BarChart3, 
  Globe, 
  Lock, 
  Headphones, 
  Smartphone 
} from 'lucide-react';

const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Shield,
      title: t('features.security'),
      description: t('features.securityDesc'),
    },
    {
      icon: Zap,
      title: t('features.fast'),
      description: t('features.fastDesc'),
    },
    {
      icon: Users,
      title: t('features.collaboration'),
      description: t('features.collaborationDesc'),
    },
    {
      icon: BarChart3,
      title: t('features.analytics'),
      description: t('features.analyticsDesc'),
    },
    {
      icon: Globe,
      title: t('features.multiLanguage'),
      description: t('features.multiLanguageDesc'),
    },
    {
      icon: Lock,
      title: t('features.privacy'),
      description: t('features.privacyDesc'),
    },
    {
      icon: Headphones,
      title: t('features.support247'),
      description: t('features.support247Desc'),
    },
    {
      icon: Smartphone,
      title: t('features.mobile'),
      description: t('features.mobileDesc'),
    },
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="section bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('features.title1')} <span className="gradient-text">{t('features.title2')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('features.subtitle')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group feature-card bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm"
              >
                {/* Icon */}
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-600 transition-colors">
                  <Icon className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors" />
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="grid md:grid-cols-2 gap-8 mt-20 bg-gradient-primary rounded-2xl p-14 text-white max-w-2xl mx-auto"
        >
          {[
            { value: '99.9%', label: t('features.uptime') },
            { value: '24/7', label: t('features.supportAvailable') },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-5xl font-bold mb-3">{stat.value}</div>
              <div className="text-white opacity-90 text-lg">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
